import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FAQ from '../components/FAQ';
import MethodSection from '../components/MethodSection';
import { HeroLoop } from '../components/marketing/HeroLoop';
import { usePageTitle } from '../hooks/usePageTitle';
import './HomePage.css';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle('AI-Powered Jury Research & Trial Strategy Platform');

  return (
    <div className="home-page">
      {/* Hero Section - Dark Navy with vLex-inspired styling */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>
              {t('home.hero.title1')}{' '}
              <span className="hero-highlight">{t('home.hero.titleHighlight')}<sup className="tm-symbol">TM</sup></span>{' '}
              {t('home.hero.title2')}
            </h1>
            <p className="hero-subtitle">
              {t('home.hero.subtitle')}
            </p>
            <div className="hero-actions">
              <Link to="/contact?type=docs" className="btn btn-primary">{t('common.requestDocumentation')}</Link>
              <Link to="/engine" className="btn btn-outline">{t('common.exploreEngine')}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Reel — JuryView in motion (split layout) */}
      <section className="hero-reel-split" aria-labelledby="hero-reel-heading">
        <div className="hero-reel-split__text">
          <div className="hero-reel-split__text-inner">
            <span className="hero-reel-eyebrow">{t('home.heroReel.eyebrow')}</span>
            <h2 id="hero-reel-heading">{t('home.heroReel.title')}</h2>
            <p>{t('home.heroReel.subtitle')}</p>
          </div>
        </div>
        <div className="hero-reel-split__stage">
          <HeroLoop variant="full" />
        </div>
      </section>

      {/* Product Showcase Section - 3 Primary Products */}
      <section className="product-showcase">
        {/* Card 1: Synthetic Juror - Rich Profile Cards */}
        <div className="showcase-card showcase-juror">
          <div className="showcase-content">
            <div className="showcase-logo">
              <span className="logo-text">{t('home.showcase.syntheticJuror.name1')}</span>
              <span className="logo-highlight">{t('home.showcase.syntheticJuror.name2')}<sup>™</sup></span>
            </div>
            <p className="showcase-tagline">{t('home.showcase.syntheticJuror.tagline')}</p>
            <p className="showcase-description">
              {t('home.showcase.syntheticJuror.description')}
            </p>
            <Link to="/engine#synthetic-juror" className="btn btn-showcase">
              {t('common.learnMore')}
            </Link>
          </div>

          <div className="showcase-illustration juror-illustration">
            {/* Stacked Juror Profile Cards */}
            <div className="juror-stack">
              {/* Background card 3 */}
              <div className="profile-card stack-3">
                <div className="profile-header"></div>
              </div>

              {/* Background card 2 */}
              <div className="profile-card stack-2">
                <div className="profile-header"></div>
              </div>

              {/* Primary juror card */}
              <div className="profile-card stack-1">
                <div className="profile-header">
                  <div className="profile-avatar">
                    <span className="avatar-initials">MR</span>
                  </div>
                  <div className="profile-info">
                    <span className="profile-name">{t('home.showcase.jurorCard.name')}</span>
                    <span className="profile-venue">{t('home.showcase.jurorCard.venue')}</span>
                  </div>
                  <div className="profile-stance plaintiff">P</div>
                </div>
                <div className="profile-details">
                  <div className="detail-row">
                    <span className="detail-label">{t('home.showcase.jurorCard.occupation')}</span>
                    <span className="detail-value">{t('home.showcase.jurorCard.occupationValue')}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">{t('home.showcase.jurorCard.education')}</span>
                    <span className="detail-value">{t('home.showcase.jurorCard.educationValue')}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">{t('home.showcase.jurorCard.anchor')}</span>
                    <span className="detail-value trait">{t('home.showcase.jurorCard.anchorValue')}</span>
                  </div>
                </div>
                <div className="profile-traits">
                  <span className="trait-tag">{t('home.showcase.jurorCard.trait1')}</span>
                  <span className="trait-tag">{t('home.showcase.jurorCard.trait2')}</span>
                </div>
              </div>
            </div>

            {/* Panel Composition Widget */}
            <div className="panel-widget">
              <div className="widget-header">{t('home.showcase.panelWidget.title')}</div>
              <div className="composition-visual">
                <div className="comp-row">
                  <div className="comp-dot plaintiff"></div>
                  <div className="comp-dot plaintiff"></div>
                  <div className="comp-dot plaintiff"></div>
                  <div className="comp-dot plaintiff"></div>
                  <div className="comp-dot undecided"></div>
                  <div className="comp-dot undecided"></div>
                </div>
                <div className="comp-row">
                  <div className="comp-dot plaintiff"></div>
                  <div className="comp-dot undecided"></div>
                  <div className="comp-dot defense"></div>
                  <div className="comp-dot defense"></div>
                  <div className="comp-dot defense"></div>
                  <div className="comp-dot defense"></div>
                </div>
              </div>
              <div className="comp-legend">
                <span><i className="dot-p"></i> 5 {t('home.showcase.panelWidget.plaintiff')}</span>
                <span><i className="dot-u"></i> 3 {t('home.showcase.panelWidget.undecided')}</span>
                <span><i className="dot-d"></i> 4 {t('home.showcase.panelWidget.defense')}</span>
              </div>
            </div>

            {/* Verdict Confidence Mini */}
            <div className="verdict-mini">
              <div className="verdict-ring">
                <svg viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="3"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="3"
                    strokeDasharray="62, 100"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="verdict-pct">62%</span>
              </div>
              <span className="verdict-label">{t('home.showcase.verdictMini.likely')}<br/>{t('home.showcase.verdictMini.label')}</span>
            </div>
          </div>
        </div>

        {/* Card 2: Fact Finder - Judge Analytics Dashboard */}
        <div className="showcase-card showcase-factfinder">
          <div className="showcase-content">
            <div className="showcase-logo">
              <span className="logo-text">{t('home.showcase.factFinder.name1')}</span>
              <span className="logo-highlight">{t('home.showcase.factFinder.name2')}<sup>™</sup></span>
            </div>
            <p className="showcase-tagline">{t('home.showcase.factFinder.tagline')}</p>
            <p className="showcase-description">
              {t('home.showcase.factFinder.description')}
            </p>
            <Link to="/engine#fact-finder" className="btn btn-showcase">
              {t('common.learnMore')}
            </Link>
          </div>

          <div className="showcase-illustration factfinder-illustration">
            {/* Judge Profile Card */}
            <div className="judge-card-main">
              <div className="judge-header">
                <div className="judge-avatar">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
                  </svg>
                </div>
                <div className="judge-info">
                  <span className="judge-name">{t('home.showcase.judgeCard.name')}</span>
                  <span className="judge-court">{t('home.showcase.judgeCard.court')}</span>
                  <span className="judge-appt">{t('home.showcase.judgeCard.appointed')}</span>
                </div>
              </div>
              <div className="judge-stats">
                <div className="stats-row">
                  <div className="stat-item">
                    <span className="stat-value">68%</span>
                    <span className="stat-label">{t('home.showcase.judgeCard.mtd')}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">41%</span>
                    <span className="stat-label">{t('home.showcase.judgeCard.msj')}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">2.3y</span>
                    <span className="stat-label">{t('home.showcase.judgeCard.avg')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* MTD Success Ring */}
            <div className="mtd-ring-card">
              <div className="mtd-ring">
                <svg viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#fef3c7" strokeWidth="10" />
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#d97706" strokeWidth="10"
                    strokeDasharray="170 82" strokeLinecap="round" transform="rotate(-90 50 50)" />
                </svg>
                <div className="mtd-center">
                  <span className="mtd-value">68%</span>
                  <span className="mtd-label">{t('home.showcase.judgeCard.mtd')}</span>
                </div>
              </div>
              <span className="mtd-title">{t('home.showcase.motionSuccess')}</span>
            </div>

            {/* Reasoning Signal Card */}
            <div className="prediction-card">
              <div className="prediction-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                </svg>
              </div>
              <div className="prediction-content">
                <span className="prediction-label">{t('home.showcase.reasoningSignal.label')}</span>
                <span className="prediction-value">{t('home.showcase.reasoningSignal.value')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Trial Signal - Analytics Dashboard */}
        <div className="showcase-card showcase-trialsignal">
          <div className="showcase-content">
            <div className="showcase-logo">
              <span className="logo-text">{t('home.showcase.trialSignal.name1')}</span>
              <span className="logo-highlight">{t('home.showcase.trialSignal.name2')}<sup>™</sup></span>
            </div>
            <p className="showcase-tagline">{t('home.showcase.trialSignal.tagline')}</p>
            <p className="showcase-description">
              {t('home.showcase.trialSignal.description')}
            </p>
            <Link to="/engine#trial-signal" className="btn btn-showcase">
              {t('common.learnMore')}
            </Link>
          </div>

          <div className="showcase-illustration signal-illustration">
            {/* Main Analytics Card */}
            <div className="analytics-card">
              <div className="analytics-header">
                <span className="analytics-title">{t('home.showcase.analytics.title')}</span>
                <span className="analytics-badge">{t('home.showcase.analytics.badge')}</span>
              </div>
              <div className="factor-bars">
                <div className="factor-row">
                  <span className="factor-name">{t('home.showcase.analytics.factor1')}</span>
                  <div className="factor-bar-container">
                    <div className="factor-bar-fill high" style={{ width: '89%' }}></div>
                  </div>
                  <span className="factor-score">89</span>
                </div>
                <div className="factor-row">
                  <span className="factor-name">{t('home.showcase.analytics.factor2')}</span>
                  <div className="factor-bar-container">
                    <div className="factor-bar-fill medium" style={{ width: '72%' }}></div>
                  </div>
                  <span className="factor-score">72</span>
                </div>
                <div className="factor-row">
                  <span className="factor-name">{t('home.showcase.analytics.factor3')}</span>
                  <div className="factor-bar-container">
                    <div className="factor-bar-fill medium" style={{ width: '65%' }}></div>
                  </div>
                  <span className="factor-score">65</span>
                </div>
                <div className="factor-row">
                  <span className="factor-name">{t('home.showcase.analytics.factor4')}</span>
                  <div className="factor-bar-container">
                    <div className="factor-bar-fill low" style={{ width: '34%' }}></div>
                  </div>
                  <span className="factor-score">34</span>
                </div>
              </div>
            </div>

            {/* Sensitivity Donut */}
            <div className="sensitivity-card">
              <div className="sensitivity-donut">
                <svg viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" strokeWidth="12" />
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#22c55e" strokeWidth="12"
                    strokeDasharray="188 64" strokeLinecap="round" transform="rotate(-90 50 50)" />
                </svg>
                <div className="donut-center">
                  <span className="donut-value">75%</span>
                  <span className="donut-label">{t('home.showcase.sensitivity.impact')}</span>
                </div>
              </div>
              <span className="sensitivity-title">{t('home.showcase.sensitivity.title')}</span>
            </div>

            {/* Key Insight Card */}
            <div className="insight-card">
              <div className="insight-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div className="insight-content">
                <span className="insight-title">{t('home.showcase.insight.title')}</span>
                <span className="insight-text">{t('home.showcase.insight.text')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary Cards Section */}
      <section className="previews section">
        <div className="container">
          <div className="preview-grid">
            <div className="preview-card">
              <h3>{t('home.preview.howItsUsed.title')}</h3>
              <p>{t('home.preview.howItsUsed.description')}</p>
              <Link to="/applications" className="preview-link">{t('home.preview.howItsUsed.link')} →</Link>
            </div>

            <div className="preview-card">
              <h3>{t('home.preview.licensing.title')}</h3>
              <p>{t('home.preview.licensing.description')}</p>
              <Link to="/licensing" className="preview-link">{t('home.preview.licensing.link')} →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Method Section — embedded narrative */}
      <MethodSection />

      {/* FAQ Section */}
      <div className="home-faq-wrapper">
        <FAQ showAll />
      </div>

      {/* Deployed Across Section */}
      <section className="deployed-across">
        <div className="container">
          <div className="deployed-content">
            <h2 className="deployed-headline">
              {t('home.deployed.headline')}
            </h2>
            <p className="deployed-description">
              {t('home.deployed.description')}
            </p>
            <div className="deployed-categories">
              <span className="category-tag">{t('home.deployed.categories.trialTeams')}</span>
              <span className="category-tag">{t('home.deployed.categories.consultants')}</span>
              <span className="category-tag">{t('home.deployed.categories.insurance')}</span>
              <span className="category-tag">{t('home.deployed.categories.legalTech')}</span>
              <span className="category-tag">{t('home.deployed.categories.enterprise')}</span>
            </div>
            <Link to="/contact" className="btn btn-deployed">
              {t('home.deployed.requestAccess')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
